import { forgetServerState } from '@/lib/query-client'
import { stopPersisting } from '@/lib/query-persist'
import { announceSignOut } from './session-channel'
export type Provider = 'google' | 'microsoft' | 'apple'

/** What login/signup resolves to: signed in, or a code was emailed for verification. */
export type AuthOutcome =
  | { status: 'authenticated' }
  | { status: 'verification_required'; email: string }

/**
 * The shape of any auth-endpoint JSON body. On success the Go service returns
 * `{ user }` or `{ verificationRequired, email }`; on error it returns a nested
 * `{ error: { type, meta } }` envelope. The pending token is NOT here — it rides
 * an httpOnly cookie the browser sends back automatically (anti-enumeration).
 */
interface AuthResponseBody {
  verificationRequired?: boolean
  email?: string
  user?: unknown
  error?: { type?: string; meta?: { message?: string } | null }
}

interface PostResult {
  status: number
  data: AuthResponseBody
}

async function postJson(path: string, body: unknown): Promise<PostResult> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  const data = (await res.json().catch(() => ({}))) as AuthResponseBody
  return { status: res.status, data }
}

/** Human-readable message for a Go error envelope. */
function messageForError(error: AuthResponseBody['error']): string {
  switch (error?.type) {
    case 'invalid_credentials':
      return 'Invalid email or password.'
    case 'weak_password':
      // Prefer WorkOS's specific strength message when present.
      return error.meta?.message ?? 'That password is too weak.'
    case 'invalid_verification_code':
      return 'That code is invalid or has expired.'
    case 'rate_limited':
      return 'Too many attempts. Please wait a moment and try again.'
    case 'sso_required':
      return 'This account signs in with SSO. Use a social button below.'
    case 'email_password_auth_disabled':
      return 'Email and password sign-in is off. Use a social button below.'
    case 'email_domain_not_allowed':
      return 'That email domain is not allowed to sign in.'
    case 'identity_link_failed':
      // The provider would not record our id. Not retryable by pressing the
      // same button, and not the person's fault — say both.
      return 'We could not link your account. Try again in a moment. If it keeps failing, ask an admin.'
    case 'mcp_handoff_unavailable':
      // The provider did not answer, so the request is still good — starting
      // over would burn a fresh one against a system that is down.
      return 'The sign-in service is not answering. Wait a moment and try again.'
    case 'mcp_authorization_failed':
      // Almost always a handoff that went stale while they signed in. The fix
      // is to start again from the app being connected, not to retry here —
      // this page no longer holds a live request.
      return 'This connection request expired or was already used. Start the connection again from the app you are connecting.'
    case 'account_disabled':
      // Deliberately not "try again" — the server sends this instead of 401
      // precisely because re-presenting the credential is the one thing that
      // will never help.
      return 'This account is disabled.'
    default:
      // Fall back to the server's own message when it carried one (e.g. a WorkOS
      // detail); genuine internal errors send `meta: null`, so those still show
      // the generic line rather than leaking a raw Go error.
      return error?.meta?.message ?? 'Something went wrong.'
  }
}

function outcomeOrThrow(res: PostResult): AuthOutcome {
  if (res.status >= 400) {
    throw new Error(messageForError(res.data.error))
  }
  if (res.data.verificationRequired && res.data.email) {
    return { status: 'verification_required', email: res.data.email }
  }
  return { status: 'authenticated' }
}

/** Email/password login. May resolve to a verification step. */
export async function login(email: string, password: string): Promise<AuthOutcome> {
  return outcomeOrThrow(await postJson('/api/auth/login', { email, password }))
}

/** Create an account. New accounts resolve to a verification step. */
export async function signUp(email: string, password: string): Promise<AuthOutcome> {
  return outcomeOrThrow(await postJson('/api/auth/register', { email, password }))
}

/**
 * Submit the emailed verification code to finish signing in. The pending token
 * is read from the httpOnly cookie set at signup — only the code is sent.
 */
export async function verifyEmail(code: string): Promise<void> {
  const res = await postJson('/api/auth/verify', { code })
  if (res.status >= 400) {
    throw new Error(messageForError(res.data.error))
  }
}

/**
 * Start real WorkOS OAuth for a social provider (full-page redirect).
 *
 * `returnTo` survives the round trip in an httpOnly cookie the server sets, so
 * a sign-in that began somewhere other than the login page — AuthKit sending
 * someone to /mcp/authorize — comes back there instead of the dashboard.
 */
export function signInWithProvider(provider: Provider, returnTo?: string): void {
  const suffix = returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : ''
  window.location.href = `/api/auth/oauth/${provider}${suffix}`
}

/**
 * Finish an AuthKit Standalone Connect flow and get the URL to hand back to the
 * MCP client. Only the id AuthKit gave us is sent — who signed in is read
 * server-side from the session, never claimed by the browser.
 */
export async function completeMcpAuthorization(externalAuthId: string): Promise<string> {
  const res = await postJson('/api/auth/mcp/authorize', { externalAuthId })
  if (res.status >= 400) {
    throw new Error(messageForError(res.data.error))
  }
  const { redirectUri } = res.data as { redirectUri?: string }
  if (!redirectUri) throw new Error('Authorization did not complete. Please try again.')
  return redirectUri
}

/** The current user, as returned by GET /api/auth/me (never the auth tokens). */
export interface Me {
  id: string
  email: string
  firstName?: string
  lastName?: string
  profilePictureUrl?: string
}

/**
 * Reads the current session. Returns null when there is no usable one.
 *
 * 401 is the ordinary signed-out case. 403 is `account_disabled` — the account
 * left `active` and the server refused the near-expiry refresh rather than
 * re-sealing the cookie. It is a signed-OUT state too, not a failure: the server
 * clears the cookie on that response, so the very next request is a plain 401.
 * Throwing here instead would put "Failed to load session." on the screen and
 * leave the router with no session and no decision.
 */
export async function getMe(): Promise<Me | null> {
  const res = await fetch('/api/auth/me', { credentials: 'include' })
  if (res.status === 401 || res.status === 403) return null
  if (!res.ok) throw new Error('Failed to load session.')
  const data = (await res.json()) as { user: Me }
  return data.user
}

/**
 * Signs out: stops writing the cache to disk and forgets every cached answer
 * (memory and disk) FIRST, so nothing of this person's survives even if the
 * request below fails, then clears the session server-side, tells the other
 * tabs, clears again, and returns to the login screen.
 */
export async function signOut(): Promise<void> {
  // From here on nothing is written to storage, so a poll landing while the
  // request below is out cannot put this person's data back on disk.
  stopPersisting()
  await forgetServerState()
  // JSON content-type is required by the endpoint's CSRF guard, even with no body.
  await fetch('/api/auth/logout', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: '{}',
  })
  announceSignOut()
  // Once more right before leaving: anything a poll put in memory meanwhile goes too.
  await forgetServerState()
  window.location.href = '/login'
}
